/* eslint-disable react/prop-types */
import { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Skeleton,
  Alert,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Analytics as AnalyticsIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import PieChartComponent from "./PieChart";
import BarChartComponent from "./BarChart";
import LineChartComponent from "./LineChart";
import useGetObservationStatistics from "@/hooks/useGetObservationStatistics";
import { useTranslation } from "react-i18next";

function ChartCard({ chartTitle, children, onClick, isLoading = false }) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card
        elevation={0}
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "200px",
          borderRadius: 2,
          bgcolor: theme.palette.dashboard.background.card,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ borderRadius: 1 }}
        />
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "200px",
        cursor: "pointer",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: theme.palette.dashboard.background.card,
        boxShadow: theme.palette.dashboard.shadow.card,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.palette.dashboard.shadow.hover,
          borderColor: "primary.main",
          bgcolor: theme.palette.dashboard.interaction.hover,
        },
      }}
      onClick={onClick}
      aria-label={t("dynamicDashboard.chartCardAria", { title: chartTitle })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <AnalyticsIcon color="primary" sx={{ fontSize: 16 }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.dashboard.text.primary,
          }}
        >
          {chartTitle}
        </Typography>
      </Box>
      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        sx={{
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </Card>
  );
}

function CompletionSummaryCard({ summaryData, isLoading = false }) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card
        elevation={0}
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "200px",
          borderRadius: 2,
          bgcolor: theme.palette.dashboard.background.card,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
        {[...Array(4)].map((_, i) => (
          <Skeleton
            key={i}
            variant="text"
            width="80%"
            height={20}
            sx={{ mb: 0.5 }}
          />
        ))}
      </Card>
    );
  }

  if (!summaryData) return null;

  const stats = [
    {
      label: t("dynamicDashboard.minLabel"),
      value: summaryData.min_duration_in_seconds / 3600,
      color: "error",
    },
    {
      label: t("dynamicDashboard.maxLabel"),
      value: summaryData.max_duration_in_seconds / 3600,
      color: "warning",
    },
    {
      label: t("dynamicDashboard.avgLabel"),
      value: summaryData.average_duration_in_seconds / 3600,
      color: "primary",
    },
    {
      label: t("dynamicDashboard.medianLabel"),
      value: summaryData.median_duration_in_seconds / 3600,
      color: "success",
    },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "200px",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: theme.palette.dashboard.background.card,
        backgroundImage: theme.palette.dashboard.gradient.primary,
        boxShadow: theme.palette.dashboard.shadow.card,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.palette.dashboard.shadow.hover,
          borderColor: "primary.main",
          backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
        },
      }}
      aria-label={t("dynamicDashboard.completionSummaryAria")}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <AnalyticsIcon color="primary" sx={{ fontSize: 16 }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.dashboard.text.primary,
          }}
        >
          {t("dynamicDashboard.completionSummaryTitle")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {stats.map(({ label, value, color }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
              borderRadius: 1,
              backgroundColor: theme.palette.dashboard.background.elevated,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 500,
                color: theme.palette.dashboard.text.secondary,
                fontSize: 11,
              }}
            >
              {label}
            </Typography>
            <Chip
              label={`${value.toFixed(1)}h`}
              color={color}
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: 10,
                height: 20,
              }}
            />
          </Box>
        ))}
      </Box>
    </Card>
  );
}

export default function DynamicDashboard() {
  const { t } = useTranslation();
  const theme = useTheme();
  const [tabValue, setTabValue] = useState("ptw");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalChart, setModalChart] = useState(null);

  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1, 0, 0);
  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59
  );
  const pad2 = (n) => String(n).padStart(2, "0");
  const defaultFrom = `${yearStart.getFullYear()}-${pad2(yearStart.getMonth() + 1)}-${pad2(yearStart.getDate())}T${pad2(yearStart.getHours())}:${pad2(yearStart.getMinutes())}`;
  const defaultTo = `${endOfToday.getFullYear()}-${pad2(endOfToday.getMonth() + 1)}-${pad2(endOfToday.getDate())}T${pad2(endOfToday.getHours())}:${pad2(endOfToday.getMinutes())}`;

  const [lineFromDate, setLineFromDate] = useState(defaultFrom);
  const [lineToDate, setLineToDate] = useState(defaultTo);

  const { counts, isLoading, error } = useGetObservationStatistics();

  const chartDataTypeLabel = t(
    tabValue === "observations"
      ? "dynamicDashboard.observationText"
      : "dynamicDashboard.workPermitText"
  );

  const chartTypeLabel = (() => {
    switch (modalChart?.type) {
      case "line":
        return t("dynamicDashboard.dailyTrendsText");
      default:
        switch (modalChart?.dataset) {
          case "ptwPerPerformer":
            return t("dynamicDashboard.perPerformerText");
          case "ptwPerAuthor":
            return t("dynamicDashboard.perAuthorText");
          case "ptwPerSite":
            return t("dynamicDashboard.perSiteText");
          case "ptwPerWorkflow":
          case "obsPerWorkflow":
            return t("dynamicDashboard.typesText");
          case "ptwMonthlyTrend":
          case "obsMonthlyTrend":
            return t("dynamicDashboard.monthlyTrendText");
          case "obsPerAuthor":
            return t("dynamicDashboard.perAuthorText");
          case "obsPerSite":
            return t("dynamicDashboard.perSiteText");
          case "obsCompletion":
            return t("dynamicDashboard.completionTimeText");
          default:
            return "";
        }
    }
  })();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChartClick = (chartType, dataset) => {
    setModalChart({ type: chartType, dataset });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalChart(null);
  };

  const renderChartInModal = () => {
    if (!modalChart) return null;
    const { type, dataset } = modalChart;
    switch (type) {
      case "line":
        return (
          <LineChartComponent
            fromDate={lineFromDate}
            toDate={lineToDate}
            setFromDate={setLineFromDate}
            setToDate={setLineToDate}
            dataType={tabValue}
            isModalView={true}
          />
        );
      case "bar":
        return (
          <BarChartComponent
            dataType={tabValue}
            selectedDataset={dataset}
            isModalView={true}
          />
        );
      case "pie":
        return (
          <PieChartComponent
            dataType={tabValue}
            selectedDataset={dataset}
            isModalView={true}
          />
        );
      default:
        return null;
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t("dynamicDashboard.errorTitle")}
          </Typography>
          <Typography variant="body2">
            {t("dynamicDashboard.errorMessage")}
          </Typography>
        </Alert>
      </Box>
    );
  }

  const renderCharts = () => {
    if (tabValue === "ptw") {
      return (
        <>
          <Box sx={{ gridArea: "line" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.ptwDailyTrendsTitle")}
              onClick={() => handleChartClick("line", null)}
              isLoading={isLoading}
            >
              <LineChartComponent
                fromDate={lineFromDate}
                toDate={lineToDate}
                setFromDate={setLineFromDate}
                setToDate={setLineToDate}
                dataType={tabValue}
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "ptwMonthlyTrend" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.ptwMonthlyTrendTitle")}
              onClick={() => handleChartClick("bar", "ptwMonthlyTrend")}
              isLoading={isLoading}
            >
              <BarChartComponent
                dataType={tabValue}
                selectedDataset="ptwMonthlyTrend"
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "ptwPerSite" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.ptwPerSiteTitle")}
              onClick={() => handleChartClick("pie", "ptwPerSite")}
              isLoading={isLoading}
            >
              <PieChartComponent
                dataType={tabValue}
                selectedDataset="ptwPerSite"
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "ptwPerAuthor" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.ptwPerAuthorTitle")}
              onClick={() => handleChartClick("pie", "ptwPerAuthor")}
              isLoading={isLoading}
            >
              <PieChartComponent
                dataType={tabValue}
                selectedDataset="ptwPerAuthor"
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "ptwPerPerformer" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.ptwPerPerformerTitle")}
              onClick={() => handleChartClick("pie", "ptwPerPerformer")}
              isLoading={isLoading}
            >
              <PieChartComponent
                dataType={tabValue}
                selectedDataset="ptwPerPerformer"
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "ptwPerWorkflow" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.ptwTypesTitle")}
              onClick={() => handleChartClick("bar", "ptwPerWorkflow")}
              isLoading={isLoading}
            >
              <BarChartComponent
                dataType={tabValue}
                selectedDataset="ptwPerWorkflow"
                isModalView={false}
              />
            </ChartCard>
          </Box>
        </>
      );
    } else {
      return (
        <>
          <Box sx={{ gridArea: "line" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.obsDailyTrendsTitle")}
              onClick={() => handleChartClick("line", null)}
              isLoading={isLoading}
            >
              <LineChartComponent
                fromDate={lineFromDate}
                toDate={lineToDate}
                setFromDate={setLineFromDate}
                setToDate={setLineToDate}
                dataType={tabValue}
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "obsMonthlyTrend" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.obsMonthlyTrendTitle")}
              onClick={() => handleChartClick("bar", "obsMonthlyTrend")}
              isLoading={isLoading}
            >
              <BarChartComponent
                dataType={tabValue}
                selectedDataset="obsMonthlyTrend"
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "obsPerSite" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.obsPerSiteTitle")}
              onClick={() => handleChartClick("pie", "obsPerSite")}
              isLoading={isLoading}
            >
              <PieChartComponent
                dataType={tabValue}
                selectedDataset="obsPerSite"
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "obsPerWorkflow" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.obsTypesTitle")}
              onClick={() => handleChartClick("pie", "obsPerWorkflow")}
              isLoading={isLoading}
            >
              <PieChartComponent
                dataType={tabValue}
                selectedDataset="obsPerWorkflow"
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "obsPerAuthor" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.obsPerAuthorTitle")}
              onClick={() => handleChartClick("pie", "obsPerAuthor")}
              isLoading={isLoading}
            >
              <PieChartComponent
                dataType={tabValue}
                selectedDataset="obsPerAuthor"
                isModalView={false}
              />
            </ChartCard>
          </Box>
          <Box sx={{ gridArea: "summary" }}>
            <CompletionSummaryCard
              summaryData={counts.obsCompletionSummary}
              isLoading={isLoading}
            />
          </Box>
          <Box sx={{ gridArea: "obsCompletion" }}>
            <ChartCard
              chartTitle={t("dynamicDashboard.completionTimeTitle")}
              onClick={() => handleChartClick("bar", "obsCompletion")}
              isLoading={isLoading}
            >
              <BarChartComponent
                dataType={tabValue}
                selectedDataset="obsCompletion"
                isModalView={false}
              />
            </ChartCard>
          </Box>
        </>
      );
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.palette.dashboard.background.main,
        display: "flex",
        flexDirection: "column",
      }}
      aria-label={t("dynamicDashboard.dashboardAria")}
    >
      {/* Compact Header Section */}
      <Box sx={{ p: 2, flexShrink: 0 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 0.5,
            color: theme.palette.dashboard.text.primary,
            fontSize: { xs: 20, sm: 24 },
          }}
        >
          {t("dynamicDashboard.dashboardTitle")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.dashboard.text.secondary,
            mb: 2,
            fontSize: 13,
          }}
        >
          {t("dynamicDashboard.dashboardSubtitle")}
        </Typography>

        {/* Compact Tabs */}
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: theme.palette.dashboard.shadow.card,
            bgcolor: theme.palette.dashboard.background.card,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: 14,
                py: 1.5,
                px: 3,
                minHeight: 44,
                color: theme.palette.dashboard.text.secondary,
              },
              "& .Mui-selected": {
                color: "primary.main",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "primary.main",
              },
            }}
            aria-label={t("dynamicDashboard.tabsAria")}
          >
            <Tab label={t("dynamicDashboard.workPermitsTab")} value="ptw" />
            <Tab
              label={t("dynamicDashboard.observationsTab")}
              value="observations"
            />
          </Tabs>
        </Card>
      </Box>

      {/* Scrollable Charts Grid */}
      <Box
        sx={{
          flexGrow: 1,
          px: 2,
          pt: 3, // Added top padding to show hover effect
          pb: 2,
          overflowY: "auto",
          overflowX: "hidden",
          height: { xs: "calc(100vh - 140px)", sm: "calc(100vh - 150px)" },
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: (theme) => alpha(theme.palette.primary.main, 0.2),
            borderRadius: "4px",
            "&:hover": {
              background: (theme) => alpha(theme.palette.primary.main, 0.3),
            },
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gridAutoRows: {
              xs: "250px",
              sm: "250px",
              md: "280px",
              lg: "280px",
            },
            gap: { xs: 2, sm: 2, md: 2.5 },
            pb: { xs: 2, sm: 3 }, // Add padding at the bottom for better scrolling
            gridTemplateAreas: {
              xs:
                tabValue === "ptw"
                  ? `
                "line"
                "ptwMonthlyTrend"
                "ptwPerSite"
                "ptwPerAuthor"
                "ptwPerPerformer"
                "ptwPerWorkflow"
              `
                  : `
                "line"
                "obsMonthlyTrend"
                "obsPerSite"
                "obsPerWorkflow"
                "obsPerAuthor"
                "obsCompletion"
                "summary"
              `,
              sm:
                tabValue === "ptw"
                  ? `
                "line line"
                "ptwMonthlyTrend ptwPerSite"
                "ptwPerAuthor ptwPerPerformer"
                "ptwPerWorkflow ptwPerWorkflow"
              `
                  : `
                "line line"
                "obsMonthlyTrend obsPerSite"
                "obsPerWorkflow obsPerAuthor"
                "obsCompletion summary"
              `,
              md:
                tabValue === "ptw"
                  ? `
                "line line ptwMonthlyTrend"
                "ptwPerSite ptwPerAuthor ptwPerPerformer"
                "ptwPerWorkflow ptwPerWorkflow ptwPerWorkflow"
              `
                  : `
                "line line obsMonthlyTrend"
                "obsPerSite obsPerWorkflow obsPerAuthor"
                "obsCompletion obsCompletion summary"
              `,
              lg:
                tabValue === "ptw"
                  ? `
                "line line ptwMonthlyTrend ptwPerSite"
                "ptwPerAuthor ptwPerPerformer ptwPerWorkflow ptwPerWorkflow"
              `
                  : `
                "line line obsMonthlyTrend obsPerSite"
                "obsPerWorkflow obsPerAuthor obsCompletion summary"
              `,
            },
          }}
        >
          {renderCharts()}
        </Box>
      </Box>

      {/* Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="xl"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            height: "85vh",
            borderRadius: 3,
            boxShadow: theme.palette.dashboard.shadow.modal,
            bgcolor: theme.palette.dashboard.background.card,
          },
        }}
        aria-labelledby="chart-modal-title"
      >
        <DialogTitle
          id="chart-modal-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 3,
            px: 4,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundImage: theme.palette.dashboard.gradient.modal,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <AnalyticsIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {chartDataTypeLabel} {chartTypeLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("dynamicDashboard.modalSubtitle")}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleModalClose}
            aria-label={t("dynamicDashboard.closeModalAria")}
            sx={{
              bgcolor: theme.palette.dashboard.background.elevated,
              "&:hover": {
                bgcolor: theme.palette.dashboard.interaction.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", p: 0 }}>
          {renderChartInModal()}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
